import React, { useState, useEffect, useMemo, useRef } from 'react';
import './CountryLookup.css';

// Data loaded from static JSON files in /public/data/

// ═══════════════════════════════════════════════════════════════════════════════
// MGI WEIGHTS — From MGI_WEIGHT_MATRICES sheet, Scenario_1_All14 (AUTHORITATIVE)
// These weights sum to 1.00
// ═══════════════════════════════════════════════════════════════════════════════
const MGI_COMPONENTS = [
  { key: 'lpi',               normKey: 'lpiNorm',               weight: 0.10, label: 'Logistics Performance (LPI)', unit: 'index', rawRange: '1-5',    source: 'World Bank LPI' },
  { key: 'water',             normKey: 'waterNorm',             weight: 0.09, label: 'Safe Water Access',           unit: '%',     rawRange: '0-100%', source: 'WHO/UNICEF JMP' },
  { key: 'sanitation',        normKey: 'sanitationNorm',        weight: 0.08, label: 'Sanitation Access',           unit: '%',     rawRange: '0-100%', source: 'WHO/UNICEF JMP' },
  { key: 'electricity',       normKey: 'electricityNorm',       weight: 0.09, label: 'Electricity Access',          unit: '%',     rawRange: '0-100%', source: 'World Bank WDI' },
  { key: 'internet',          normKey: 'internetNorm',          weight: 0.08, label: 'Internet Penetration',        unit: '%',     rawRange: '0-100%', source: 'ITU / World Bank' },
  { key: 'education',         normKey: 'educationNorm',         weight: 0.08, label: 'Tertiary Education',          unit: '%',     rawRange: '0-150%', source: 'UNESCO' },
  { key: 'bank',              normKey: 'bankNorm',              weight: 0.07, label: 'Bank Account Penetration',    unit: '%',     rawRange: '0-100%', source: 'Global Findex' },
  { key: 'capci',             normKey: 'capciNorm',             weight: 0.07, label: 'CAPCI (Credit-Augmented PCI)',unit: 'USD',   rawRange: '$0+',    source: 'Calculated: GDP×(1+0.435×FAM)' },
  { key: 'gcr',               normKey: 'gcrNorm',               weight: 0.06, label: 'Grassroots Credit Ratio',     unit: 'ratio', rawRange: '0-3+',   source: 'Calculated: HH Debt/(Pop×GDP_pc)' },
  { key: 'ruleOfLaw',         normKey: 'ruleOfLawNorm',         weight: 0.06, label: 'Rule of Law',                 unit: 'index', rawRange: '-2.5 to +2.5', source: 'World Bank WGI' },
  { key: 'realWage',          normKey: 'realWageNorm',          weight: 0.06, label: 'Real Wage Growth',            unit: 'index', rawRange: '0+',     source: 'ILO / UNSTAT' },
  { key: 'insurance',         normKey: 'insuranceNorm',         weight: 0.05, label: 'Insurance Penetration',       unit: '% GDP', rawRange: '0-30%',  source: 'Swiss Re Sigma' },
  { key: 'corruptionControl', normKey: 'corruptionControlNorm', weight: 0.05, label: 'Control of Corruption',       unit: 'index', rawRange: '-2.5 to +2.5', source: 'World Bank WGI' },
  { key: 'physicians',        normKey: 'physiciansNorm',        weight: 0.06, label: 'Physicians per 1,000',        unit: 'per 1k',rawRange: '0-7',    source: 'WHO / World Bank' },
];

const WEIGHT_SUM = MGI_COMPONENTS.reduce((s, c) => s + c.weight, 0); // 1.00

// Additional raw metrics (not MGI components) for supplementary display
const SUPPLEMENTARY_METRICS = [
  { key: 'gdpPerCapita',  label: 'GDP Per Capita',           unit: 'USD',   format: 'currency' },
  { key: 'gniPerCapita',  label: 'GNI Per Capita',           unit: 'USD',   format: 'currency' },
  { key: 'capci',         label: 'CAPCI',                    unit: 'USD',   format: 'currency' },
  { key: 'govDebt',       label: 'Government Debt (% GDP)',  unit: '%',     format: 'pct' },
  { key: 'dti',           label: 'Debt-to-Income Ratio',     unit: '%',     format: 'pct' },
  { key: 'fam',           label: 'Finance Multiplier (FAM)', unit: '',      format: 'decimal' },
  { key: 'hhDebtPctGdp',  label: 'Household Debt (% GDP)',   unit: '%',     format: 'pct' },
  { key: 'gii',           label: 'Gender Inequality Index',  unit: 'index', format: 'decimal' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// Compute MGI from normalized components (with reweighting for missing data)
// This reproduces the workbook's MGI_1 calculation
// ═══════════════════════════════════════════════════════════════════════════════
function computeMGI(countryData) {
  let weightedSum = 0;
  let availableWeight = 0;
  const contributions = [];

  for (const comp of MGI_COMPONENTS) {
    const normVal = countryData[comp.normKey];
    if (normVal !== null && normVal !== undefined && !isNaN(normVal)) {
      weightedSum += normVal * comp.weight;
      availableWeight += comp.weight;
      contributions.push({ ...comp, normVal });
    }
  }

  if (availableWeight === 0) return { score: null, contributions: [] };

  // Reweight: scale up to account for missing components
  const mgi = weightedSum / availableWeight;
  return {
    score: Math.round(mgi * 100) / 100,
    contributions: contributions.map(c => ({
      ...c,
      reweightedWeight: (c.weight / availableWeight),
      contribution: c.normVal * (c.weight / availableWeight),
    })),
    availableCount: contributions.length,
    totalCount: MGI_COMPONENTS.length,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// Format helpers
// ═══════════════════════════════════════════════════════════════════════════════
function formatValue(val, format) {
  if (val === null || val === undefined || isNaN(val)) return '—';
  switch (format) {
    case 'currency': return '$' + Number(val).toLocaleString('en-US', { maximumFractionDigits: 0 });
    case 'pct': return Number(val).toFixed(1) + '%';
    case 'decimal': return Number(val).toFixed(4);
    default: return Number(val).toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
}

function formatRaw(val, unit) {
  if (val === null || val === undefined || isNaN(val)) return '—';
  if (unit === 'USD') return '$' + Number(val).toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (unit === '%' || unit === '% GDP') return Number(val).toFixed(2) + '%';
  if (unit === 'index') return Number(val).toFixed(4);
  if (unit === 'ratio') return Number(val).toFixed(4);
  if (unit === 'per 1k') return Number(val).toFixed(2);
  return Number(val).toFixed(2);
}

function mgiGrade(score) {
  if (score === null || score === undefined) return { letter: '—', color: '#94a3b8', label: 'No Data' };
  if (score >= 80) return { letter: 'A', color: '#059669', label: 'Excellent' };
  if (score >= 65) return { letter: 'B', color: '#0284c7', label: 'Good' };
  if (score >= 50) return { letter: 'C', color: '#d97706', label: 'Average' };
  if (score >= 35) return { letter: 'D', color: '#ea580c', label: 'Below Average' };
  return { letter: 'F', color: '#dc2626', label: 'Weak' };
}

// ═══════════════════════════════════════════════════════════════════════════════
// Components
// ═══════════════════════════════════════════════════════════════════════════════

function MGIScoreCard({ data, year }) {
  const mgi = data.mgi1;
  const grade = mgiGrade(mgi);

  return (
    <div className="mgi-score-card">
      <div className="mgi-score-left">
        <div className="mgi-label">Macro-Governance Index (MGI)</div>
        <div className="mgi-year">{data.name} — {year}</div>
      </div>
      <div className="mgi-score-center">
        <div className="mgi-score-number" style={{ color: grade.color }}>
          {mgi !== null ? mgi.toFixed(2) : '—'}
        </div>
        <div className="mgi-score-max">/ 100</div>
      </div>
      <div className="mgi-score-right">
        <div className="mgi-grade-badge" style={{ backgroundColor: grade.color }}>
          {grade.letter}
        </div>
        <div className="mgi-grade-label">{grade.label}</div>
      </div>
    </div>
  );
}

function ComponentBreakdown({ data }) {
  const mgiCalc = computeMGI(data);

  return (
    <div className="component-breakdown">
      <h3 className="section-title">14 MGI Components — Normalized Percentiles</h3>
      <p className="section-subtitle">
        {mgiCalc.availableCount < mgiCalc.totalCount
          ? `${mgiCalc.availableCount} of ${mgiCalc.totalCount} components available. Weights rebalanced to compensate.`
          : `All ${mgiCalc.totalCount} components available.`
        }
      </p>
      <div className="components-grid">
        {MGI_COMPONENTS.map(comp => {
          const normVal = data[comp.normKey];
          const rawVal = data[comp.key];
          const isAvailable = normVal !== null && normVal !== undefined && !isNaN(normVal);
          const pct = isAvailable ? normVal : 0;

          return (
            <div key={comp.key} className={`component-card ${!isAvailable ? 'unavailable' : ''}`}>
              <div className="comp-header">
                <span className="comp-label">{comp.label}</span>
                <span className="comp-weight">
                  {(comp.weight * 100).toFixed(0)}%
                </span>
              </div>
              <div className="comp-bar-container">
                <div
                  className="comp-bar"
                  style={{
                    width: `${Math.min(100, pct)}%`,
                    backgroundColor: isAvailable ? barColor(pct) : '#e2e8f0'
                  }}
                />
              </div>
              <div className="comp-values">
                <span className="comp-norm">
                  {isAvailable ? `${normVal.toFixed(1)}%` : 'N/A'}
                </span>
                <span className="comp-raw">
                  Raw: {formatRaw(rawVal, comp.unit)}
                </span>
              </div>
              <div className="comp-source">{comp.source}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function barColor(pct) {
  if (pct >= 80) return '#059669';
  if (pct >= 60) return '#0284c7';
  if (pct >= 40) return '#d97706';
  if (pct >= 20) return '#ea580c';
  return '#dc2626';
}

function SupplementaryMetrics({ data }) {
  return (
    <div className="supplementary-section">
      <h3 className="section-title">Additional Indicators</h3>
      <div className="supp-grid">
        {SUPPLEMENTARY_METRICS.map(m => {
          const val = data[m.key];
          return (
            <div key={m.key} className="supp-card">
              <div className="supp-label">{m.label}</div>
              <div className="supp-value">{formatValue(val, m.format)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HistoryChart({ history, countryName }) {
  const [chartMetric, setChartMetric] = useState('mgi1');

  const CHART_METRICS = [
    { key: 'mgi1', label: 'MGI Score', unit: 'Score (0–100)' },
    { key: 'lpiNorm', label: 'LPI (Normalized)', unit: 'Percentile (0–100)' },
    { key: 'waterNorm', label: 'Water Access (Normalized)', unit: '%' },
    { key: 'sanitationNorm', label: 'Sanitation (Normalized)', unit: '%' },
    { key: 'electricityNorm', label: 'Electricity (Normalized)', unit: '%' },
    { key: 'internetNorm', label: 'Internet (Normalized)', unit: '%' },
    { key: 'educationNorm', label: 'Education (Normalized)', unit: '%' },
    { key: 'bankNorm', label: 'Bank Access (Normalized)', unit: '%' },
    { key: 'capciNorm', label: 'CAPCI (Normalized)', unit: 'Percentile (0–100)' },
    { key: 'gcrNorm', label: 'GCR (Normalized)', unit: 'Percentile (0–100)' },
    { key: 'ruleOfLawNorm', label: 'Rule of Law (Normalized)', unit: 'Percentile (0–100)' },
    { key: 'realWageNorm', label: 'Real Wage (Normalized)', unit: 'Percentile (0–100)' },
    { key: 'insuranceNorm', label: 'Insurance (Normalized)', unit: 'Percentile (0–100)' },
    { key: 'corruptionControlNorm', label: 'Corruption Control (Normalized)', unit: 'Percentile (0–100)' },
    { key: 'physiciansNorm', label: 'Physicians (Normalized)', unit: 'Percentile (0–100)' },
    { key: 'lpi', label: 'LPI (Raw)', unit: 'Index (1–5)' },
    { key: 'water', label: 'Water Access % (Raw)', unit: '%' },
    { key: 'sanitation', label: 'Sanitation % (Raw)', unit: '%' },
    { key: 'electricity', label: 'Electricity % (Raw)', unit: '%' },
    { key: 'internet', label: 'Internet % (Raw)', unit: '%' },
    { key: 'education', label: 'Education Enrollment % (Raw)', unit: '% Gross Enrollment' },
    { key: 'bank', label: 'Bank Account % (Raw)', unit: '%' },
    { key: 'capci', label: 'CAPCI $ (Raw)', unit: 'USD' },
    { key: 'gcr', label: 'GCR Ratio (Raw)', unit: 'Ratio' },
    { key: 'ruleOfLaw', label: 'Rule of Law Index (Raw)', unit: 'Index (−2.5 to 2.5)' },
    { key: 'realWage', label: 'Real Wage Index (Raw)', unit: 'Index' },
    { key: 'insurance', label: 'Insurance % GDP (Raw)', unit: '% of GDP' },
    { key: 'corruptionControl', label: 'Corruption Control Index (Raw)', unit: 'Index (−2.5 to 2.5)' },
    { key: 'physicians', label: 'Physicians per 1k (Raw)', unit: 'Per 1,000 pop.' },
    { key: 'gdpPerCapita', label: 'GDP Per Capita $', unit: 'USD' },
    { key: 'gniPerCapita', label: 'GNI Per Capita $', unit: 'USD' },
    { key: 'govDebt', label: 'Government Debt % GDP', unit: '% of GDP' },
    { key: 'dti', label: 'Debt-to-Income %', unit: '%' },
  ];

  const years = Object.keys(history).sort();
  const values = years.map(y => history[y][chartMetric]);
  const validValues = values.filter(v => v !== null && v !== undefined);
  const metricLabel = CHART_METRICS.find(m => m.key === chartMetric)?.label || chartMetric;
  const metricUnit = CHART_METRICS.find(m => m.key === chartMetric)?.unit || '';

  if (validValues.length < 2) return (
    <div className="history-section">
      <div className="history-header">
        <h3 className="section-title">Historical Trend — {countryName}</h3>
        <select value={chartMetric} onChange={e => setChartMetric(e.target.value)} className="chart-metric-select">
          {CHART_METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      </div>
      <p style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
        Not enough data to chart {metricLabel} for this country.
      </p>
    </div>
  );

  const maxVal = Math.max(...validValues);
  const minVal = Math.min(...validValues);
  const padding = (maxVal - minVal) * 0.1 || 1;
  const yMax = maxVal + padding;
  const yMin = Math.max(0, minVal - padding);
  const range = yMax - yMin || 1;

  // Generate nice Y-axis ticks
  const tickCount = 5;
  const tickStep = range / (tickCount - 1);
  const yTicks = Array.from({ length: tickCount }, (_, i) => yMin + i * tickStep);

  const chartWidth = 720;
  const chartHeight = 220;
  const padL = 72, padR = 15, padT = 15, padB = 35;
  const w = chartWidth - padL - padR;
  const h = chartHeight - padT - padB;

  const points = years.map((yr, i) => {
    const x = padL + (i / (years.length - 1)) * w;
    const val = values[i];
    const y = val !== null && val !== undefined ? padT + h - ((val - yMin) / range) * h : null;
    return { x, y, yr, val };
  }).filter(p => p.y !== null);

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  // Format tick labels based on metric type
  const formatTick = (v) => {
    if (chartMetric === 'capci' || chartMetric === 'gdpPerCapita' || chartMetric === 'gniPerCapita') {
      return '$' + (v >= 1000 ? Math.round(v / 1000) + 'k' : Math.round(v));
    }
    if (v >= 100) return Math.round(v).toLocaleString();
    if (v >= 10) return v.toFixed(1);
    return v.toFixed(2);
  };

  return (
    <div className="history-section">
      <div className="history-header">
        <h3 className="section-title">Historical Trend — {countryName}</h3>
        <select value={chartMetric} onChange={e => setChartMetric(e.target.value)} className="chart-metric-select">
          {CHART_METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      </div>
      <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="history-chart">
        {/* Y-axis unit label */}
        <text
          x={14}
          y={padT + h / 2}
          textAnchor="middle"
          fontSize="10"
          fill="#94a3b8"
          transform={`rotate(-90, 14, ${padT + h / 2})`}
        >{metricUnit}</text>
        {yTicks.map(v => {
          const y = padT + h - ((v - yMin) / range) * h;
          return (
            <g key={v}>
              <line x1={padL} y1={y} x2={padL + w} y2={y} stroke="#e2e8f0" strokeWidth="1" />
              <text x={padL - 5} y={y + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{formatTick(v)}</text>
            </g>
          );
        })}
        {years.filter((_, i) => i % 5 === 0 || i === years.length - 1).map(yr => {
          const i = years.indexOf(yr);
          const x = padL + (i / (years.length - 1)) * w;
          return (
            <text key={yr} x={x} y={chartHeight - 5} textAnchor="middle" fontSize="10" fill="#94a3b8">{yr}</text>
          );
        })}
        <path d={pathD} fill="none" stroke="#0f766e" strokeWidth="2.5" strokeLinejoin="round" />
        {points.map(p => (
          <circle key={p.yr} cx={p.x} cy={p.y} r="3" fill="#0f766e" />
        ))}
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main CountryLookup Page
// ═══════════════════════════════════════════════════════════════════════════════

export default function CountryLookup() {
  const [allData, setAllData] = useState(null);       // full dataset: { "2000": [...], ... }
  const [countries, setCountries] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [selectedYear, setSelectedYear] = useState('2023');
  const [countryData, setCountryData] = useState(null);
  const [history, setHistory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load all data once on mount
  useEffect(() => {
    async function loadData() {
      try {
        const [dataRes, countriesRes] = await Promise.all([
          fetch(`${process.env.PUBLIC_URL}/data/grademygovernment_data.json`),
          fetch(`${process.env.PUBLIC_URL}/data/countries.json`),
        ]);
        const data = await dataRes.json();
        const countryList = await countriesRes.json();
        setAllData(data);
        setCountries(countryList);
        setYears(Object.keys(data).sort());
      } catch (err) {
        console.error('Error loading data:', err);
      }
      setLoading(false);
    }
    loadData();
  }, []);

  // When selection changes, find the country from in-memory data
  useEffect(() => {
    if (!allData || !selectedCode) return;

    // Get data for selected year
    const yearRecords = allData[selectedYear];
    if (!yearRecords) return;
    const country = yearRecords.find(c => c.code === selectedCode.toUpperCase());
    setCountryData(country || null);

    // Build history across all years
    const hist = {};
    for (const [yr, records] of Object.entries(allData)) {
      const match = records.find(c => c.code === selectedCode.toUpperCase());
      if (match) hist[yr] = match;
    }
    setHistory(Object.keys(hist).length > 0 ? hist : null);
  }, [allData, selectedCode, selectedYear]);

  // Filtered countries for search
  const filteredCountries = useMemo(() => {
    if (!searchTerm) return countries;
    const term = searchTerm.toLowerCase();
    return countries.filter(c =>
      c.name.toLowerCase().includes(term) || c.code.toLowerCase().includes(term)
    );
  }, [countries, searchTerm]);

  const handleSelect = (code, name) => {
    setSelectedCode(code);
    setSearchTerm(name);
    setShowDropdown(false);
  };

  return (
    <div className="country-lookup">
      {/* Hero header */}
      <div className="lookup-hero">
        <h1 className="hero-title">Grade My Government</h1>
        <p className="hero-tagline">A citizen and policymaker's tool to see what's working — and what to work on</p>
        <p className="hero-subtitle">
          Interactive companion to <em>Macro-Governance Theory: A Quantitative Framework for Measuring and Enhancing Jurisdictional Wellbeing</em>.
          Look up any country's Macro-Governance Index — a data-driven, non-political measure
          of governance infrastructure based on publicly available data.
        </p>
      </div>

      {/* Search controls */}
      <div className="search-controls">
        <div className="search-field">
          <label>Country</label>
          <div className="search-input-wrapper" ref={dropdownRef}>
            <input
              type="text"
              placeholder="Search country..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              className="search-input"
            />
            {showDropdown && filteredCountries.length > 0 && (
              <div className="search-dropdown">
                {filteredCountries.map(c => (
                  <div
                    key={c.code}
                    className="dropdown-item"
                    onClick={() => handleSelect(c.code, c.name)}
                  >
                    <span className="dropdown-code">{c.code}</span>
                    <span className="dropdown-name">{c.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="year-field">
          <label>Year</label>
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(e.target.value)}
            className="year-select"
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Results */}
      {loading && <div className="loading">Loading governance data...</div>}

      {!loading && countryData && (
        <div className="results-container">
          <MGIScoreCard data={countryData} year={selectedYear} />
          <ComponentBreakdown data={countryData} />
          {history && <HistoryChart history={history} countryName={countryData.name} />}
          <SupplementaryMetrics data={countryData} />
        </div>
      )}

      {!countryData && !loading && (
        <div className="empty-state">
          <p>Select a country and year above to view governance data.</p>
          <p className="empty-hint">
            The Macro-Governance Index (MGI) measures 14 key governance and development
            indicators across 170 countries from 2000 to 2025.
          </p>
        </div>
      )}
    </div>
  );
}
